import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Badge,
  Skeleton,
  Pagination,
  EmptyState,
  Alert,
  FilterBar,
  SavedFiltersMenu,
  ListSummaryBar,
} from '@bbpos/ui';
import type { FilterBarFacet, FilterBarSelect } from '@bbpos/ui';
import type { Bill } from '@bbpos/types';
import { billingService } from '@/services/billing.service';
import { ApiError } from '@/lib/api-client';
import { formatCurrency, formatDateTime } from '@/lib/format';
import { getLocationTimezone } from '@/lib/tenant-clock';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { usePosListFilterBar } from '@/hooks/use-list-filter-bar';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNetworkStore } from '@/stores/use-network-store';
import { STATUS_BADGE_MAP, formatBillNumber } from '@/lib/bill-constants';

// ─── Helpers ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'VOID', label: 'Void' },
  { value: 'DRAFT', label: 'Draft' },
];

const PAYMENT_OPTIONS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CARD', label: 'Card' },
  { value: 'WALLET', label: 'Wallet' },
  { value: 'SPLIT', label: 'Split' },
  { value: 'CREDIT', label: 'Credit' },
];

const PAGE_SIZE = 20;

const STATUS_LABELS: Record<string, string> = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.value, o.label]));
const PAYMENT_LABELS: Record<string, string> = Object.fromEntries(PAYMENT_OPTIONS.map((o) => [o.value, o.label]));

// T27: the shared filter bar — the same filters, favourites and export as the
// admin Bills page (both list GET /bills, so favourites are shared).
const BILL_FILTER_KEYS = [
  'customer_name',
  'customer_phone',
  'bill_number',
  'search',
  'status',
  'payment_method',
  'location_id',
  'start_date',
  'end_date',
];
const BILL_FACETS: FilterBarFacet[] = [
  { key: 'customer_name', label: 'Customer' },
  { key: 'customer_phone', label: 'Mobile', kind: 'phone' },
  { key: 'bill_number', label: 'Bill no.', kind: 'number' },
  // Bill number, customer or mobile in one — what the old single search box did.
  { key: 'search', label: 'Any of them' },
];
const BILL_DATE = { fromKey: 'start_date', toKey: 'end_date' };

/** +919876543210 → +91 98765 43210 (Indian mobile); other numbers unchanged. */
function formatPhone(phone: string): string {
  const m = /^\+91(\d{5})(\d{5})$/.exec(phone);
  return m ? `+91 ${m[1]} ${m[2]}` : phone;
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function ReceiptIcon() {
  return (
    <svg className="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2-3-2z" />
      <line x1="8" y1="8" x2="16" y2="8" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="8" y1="16" x2="12" y2="16" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function BillHistoryPage() {
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const [searchParams] = useSearchParams();
  const isConnected = useNetworkStore((s) => s.isConnected);

  // Every filter lives in the URL (no date filter unless one is asked for: an
  // empty list from a silent "Today" looked like a broken filter). Days are the
  // store's (T18) — the filtered store's, else the business's.
  const bar = usePosListFilterBar({
    page: 'bills',
    keys: BILL_FILTER_KEYS,
    date: BILL_DATE,
    locationKey: 'location_id',
    online: isConnected,
  });
  const { values } = bar;
  const page = Number(searchParams.get('page')) || 1;

  // FR-BILL-013: location filter over the user's assigned active locations.
  // Only offered when there is more than one to choose between.
  const profileLocations = useAuthStore((s) => s.profile?.locations);
  const activeLocations = (profileLocations ?? []).filter((l) => l.is_active);
  const selects: FilterBarSelect[] = [
    { key: 'status', label: 'Status', options: STATUS_OPTIONS, allLabel: 'All statuses' },
    { key: 'payment_method', label: 'Payment', options: PAYMENT_OPTIONS, allLabel: 'All payments' },
  ];
  if (activeLocations.length > 1) {
    selects.push({
      key: 'location_id',
      label: 'Location',
      options: activeLocations.map((l) => ({ value: l.id, label: l.name })),
      allLabel: 'All locations',
    });
  }

  const [bills, setBills] = useState<Bill[]>([]);
  const [meta, setMeta] = useState<{
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    summary?: { grand_total: number };
  }>({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // ── Fetch bills ──────────────────────────────────────────────────────────
  // Offline the list keeps what it last loaded (no request, no error) and
  // refetches when the connection is back.
  useEffect(() => {
    if (!isConnected) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    async function fetchBills() {
      setIsLoading(true);
      setError(null);
      try {
        const result = await billingService.listBills({ ...values, page, limit: PAGE_SIZE });
        if (cancelled) return;

        // listBills returns { data, meta } via apiClient which unwraps .data
        // So result is { data: Bill[], meta: PaginationMeta }
        setBills(result.data);
        setMeta(result.meta);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Failed to load bills. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchBills();
    return () => { cancelled = true; };
  }, [values, page, isConnected]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleRowClick = useCallback((billId: string) => {
    navigate(`/pos/bills/${billId}`);
  }, [navigate]);

  const handleResume = useCallback((billId: string) => {
    navigate(`/pos/billing?resume=${billId}`);
  }, [navigate]);

  // Every bill the filters match, all pages — the API writes the file (T20/T27).
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await billingService.exportBills(values);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bills.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  // Return-flow shortcut: cashier hit "Return" in the cart-mode selector
  // and was routed here to pick the original bill. Surfaced as a banner so
  // they understand what the page is asking them to do.
  const isReturnFlow = searchParams.get('action') === 'return';

  return (
    <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
      {/* ═══ Header ═══ */}
      <header className="shrink-0 flex items-center gap-4 px-6 h-14 bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-lg animate-fade-in">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/pos/billing')}
          className="text-white/80 hover:text-white hover:bg-white/10"
          aria-label="Back to POS"
        >
          <ChevronLeftIcon />
          <span className="ml-1 hidden sm:inline">POS</span>
        </Button>
        <h1 className="text-base font-bold">
          {isReturnFlow ? 'Select bill to return' : 'Bill History'}
        </h1>
        {/* T03: where a cashier follows their returns (RINV number + status). */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/pos/returns')}
          className="text-white/90 hover:text-white hover:bg-white/10"
        >
          Returns
        </Button>
      </header>

      {isReturnFlow && (
        <div
          role="status"
          aria-live="polite"
          className="shrink-0 px-6 py-2 bg-danger-50 dark:bg-danger-900/30 text-danger-800 dark:text-danger-200 text-sm border-b border-danger-200 dark:border-danger-800/60"
        >
          🔁 Pick the original bill, then tap <strong>Process Return</strong> on its
          detail page to issue a refund or credit note.
        </div>
      )}

      {/* ═══ Filters (T27) ═══ */}
      <div className="shrink-0 px-6 py-3 border-b border-neutral-200 dark:border-neutral-700">
        <FilterBar
          facets={BILL_FACETS}
          selects={selects}
          date={BILL_DATE}
          values={values}
          onChange={bar.onChange}
          onReset={bar.onReset}
          timeZone={bar.timeZone}
          placeholder="Type a customer, mobile or bill number… (drafts by customer)"
          favorites={<SavedFiltersMenu {...bar.favorites} />}
          actions={
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExport}
              disabled={!isConnected || meta.total === 0 || isExporting}
            >
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          }
        />
      </div>

      {/* ═══ Summary: every matching bill, not the page — the export's count and sum ═══ */}
      {!isLoading && bills.length > 0 && (
        <div className="shrink-0 px-6 py-3 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
          <ListSummaryBar
            items={[
              { label: 'Matching bills', value: meta.total.toLocaleString('en-IN'), tone: 'brand' },
              {
                label: 'Total of these bills',
                value: formatCurrency(meta.summary?.grand_total ?? 0, currency),
                tone: 'success',
              },
            ]}
          />
        </div>
      )}

      {/* ═══ Error banner ═══ */}
      {error && (
        <div className="px-6 pt-3 shrink-0">
          <Alert variant="danger" description={error} dismissible onDismiss={() => setError(null)} />
        </div>
      )}

      {/* ═══ Bills List ═══ */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-6 space-y-3">
            <Skeleton variant="table-row" count={8} animated />
          </div>
        ) : bills.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<ReceiptIcon />}
            title={isConnected ? 'No bills found' : "You're offline"}
            description={
              isConnected
                ? 'Try adjusting your filters or search query.'
                : 'Bill history loads when the connection is back. Bills made offline sync automatically.'
            }
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col className="w-[18%]" />
                  <col className="w-[16%]" />
                  <col className="w-[22%]" />
                  <col className="w-[14%]" />
                  <col className="w-[12%]" />
                  <col className="w-[18%]" />
                </colgroup>
                <thead className="bg-neutral-100 dark:bg-neutral-800 sticky top-0 z-10">
                  <tr className="text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    <th className="px-6 py-3">Bill Number</th>
                    <th className="px-6 py-3">Date / Time</th>
                    <th className="px-6 py-3">Customer</th>
                    <th className="px-6 py-3 text-right">Grand Total</th>
                    <th className="px-6 py-3">Payment</th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {bills.map((bill) => (
                    <tr
                      key={bill.id}
                      onClick={() => handleRowClick(bill.id)}
                      className={[
                        'even:bg-neutral-50 dark:even:bg-neutral-800/50 hover:bg-brand-50 dark:hover:bg-brand-900/20',
                        'focus-within:bg-brand-50 dark:focus-within:bg-brand-900/20 cursor-pointer',
                        'transition-all duration-200 animate-slide-up',
                        bill.status === 'DRAFT' ? 'bg-white dark:bg-neutral-900 text-neutral-500' : 'bg-white dark:bg-neutral-900',
                      ].join(' ')}
                    >
                      <td className="px-6 py-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(bill.id);
                          }}
                          aria-label={`Open bill ${formatBillNumber(bill)}`}
                          className="font-medium text-neutral-900 dark:text-neutral-100 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                        >
                          {formatBillNumber(bill)}
                        </button>
                      </td>
                      <td className="px-6 py-3 text-neutral-600 dark:text-neutral-400 tabular-nums">
                        {formatDateTime(bill.created_at, getLocationTimezone(bill.location_id))}
                      </td>
                      <td className="px-6 py-3 text-neutral-700 dark:text-neutral-300 truncate">
                        {bill.customer_name || <span className="text-neutral-400">Walk-in</span>}
                        {bill.customer_phone && (
                          <span className="block text-xs text-neutral-400 tabular-nums">{formatPhone(bill.customer_phone)}</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right font-medium text-neutral-900 dark:text-neutral-100 tabular-nums">
                        {formatCurrency(bill.grand_total + (bill.rounding_adjustment ?? 0), currency)}
                      </td>
                      <td className="px-6 py-3">
                        {bill.payment_method ? (
                          <Badge variant="info" size="sm">
                            {PAYMENT_LABELS[bill.payment_method] ?? bill.payment_method}
                          </Badge>
                        ) : (
                          <span className="text-neutral-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <Badge variant={STATUS_BADGE_MAP[bill.status]} size="sm" dot>
                            {STATUS_LABELS[bill.status] ?? bill.status}
                          </Badge>
                          {bill.status === 'DRAFT' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResume(bill.id);
                              }}
                              aria-label={`Resume bill ${formatBillNumber(bill)}`}
                            >
                              Resume
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden px-4 py-3 space-y-2">
              {bills.map((bill) => (
                <button
                  key={bill.id}
                  type="button"
                  onClick={() => handleRowClick(bill.id)}
                  className="w-full text-left bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 hover:bg-brand-50 dark:hover:bg-brand-900/20 hover:shadow-md transition-all duration-200 animate-slide-up"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100">
                      {formatBillNumber(bill)}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Badge variant={STATUS_BADGE_MAP[bill.status]} size="sm" dot>
                        {STATUS_LABELS[bill.status] ?? bill.status}
                      </Badge>
                      {bill.status === 'DRAFT' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResume(bill.id);
                          }}
                          aria-label={`Resume bill ${formatBillNumber(bill)}`}
                        >
                          Resume
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
                    <span>{formatDateTime(bill.created_at, getLocationTimezone(bill.location_id))}</span>
                    <span>{bill.payment_method ? PAYMENT_LABELS[bill.payment_method] ?? bill.payment_method : ''}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-neutral-600 dark:text-neutral-400">
                      {bill.customer_name || 'Walk-in'}
                      {bill.customer_phone && (
                        <span className="ml-1 text-neutral-400">{formatPhone(bill.customer_phone)}</span>
                      )}
                    </span>
                    <span className="font-semibold text-sm text-neutral-900 dark:text-neutral-100">
                      {formatCurrency(bill.grand_total + (bill.rounding_adjustment ?? 0), currency)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ═══ Pagination ═══ */}
      {!isLoading && meta.totalPages > 0 && (
        <div className="shrink-0 px-6 py-3 bg-white dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700">
          <Pagination
            currentPage={meta.page}
            totalPages={meta.totalPages}
            totalItems={meta.total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => bar.onChange({ page: String(p) })}
          />
        </div>
      )}
    </div>
  );
}
